import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const DashboardTab = ({ data, t }) => {
    const { t: _t } = useTranslation();
    const tx = t || _t;
    const fallback = tx('receptionOutpatients.details.fallback.na', { defaultValue: 'N/A' });

    const admissionDate = useMemo(() => {
        if (!data?.visit_date) return fallback;
        // supports both "YYYY-MM-DD HH:mm:ss" and ISO strings
        if (typeof data.visit_date === 'string') return data.visit_date.split(' ')[0];
        try {
            return new Date(data.visit_date).toISOString().split('T')[0];
        } catch {
            return fallback;
        }
    }, [data?.visit_date, fallback]);

    const assignedDoctor = useMemo(() => {
        const v = data?.vet;
        return v?.full_name || `${v?.first_name || ''} ${v?.last_name || ''}`.trim() || fallback;
    }, [data?.vet, fallback]);

    // Expected checkout: explicit follow-up date, else admission + planned stay days
    const expectedCheckout = useMemo(() => {
        if (data?.follow_up_date) return String(data.follow_up_date).split(' ')[0];
        if (data?.visit_date && data?.length_of_stay_days) {
            try {
                const date = new Date(data.visit_date);
                date.setDate(date.getDate() + parseInt(data.length_of_stay_days, 10));
                return date.toISOString().split('T')[0];
            } catch {
                return '—';
            }
        }
        return '—';
    }, [data?.follow_up_date, data?.visit_date, data?.length_of_stay_days]);

    // Day of stay: manual length_of_stay_days takes precedence (same rule as the
    // billing/ops-hub derivation); otherwise count from admission to today (or checkout).
    const stayDays = useMemo(() => {
        const manual = parseInt(data?.length_of_stay_days, 10);
        if (!Number.isNaN(manual) && manual > 0) return manual;
        if (data?.billable_stay_days != null) {
            const billable = parseInt(data.billable_stay_days, 10);
            if (!Number.isNaN(billable) && billable > 0) return billable;
        }
        if (!data?.visit_date) return null;
        try {
            const start = new Date(data.visit_date);
            start.setHours(0, 0, 0, 0);
            const endRaw = data?.checked_out_at || data?.checkout_date;
            const end = endRaw ? new Date(endRaw) : new Date();
            end.setHours(0, 0, 0, 0);
            const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
            return days > 0 ? days : 1;
        } catch {
            return null;
        }
    }, [data?.visit_date, data?.length_of_stay_days, data?.billable_stay_days, data?.checked_out_at, data?.checkout_date]);

    if (!data) return null;

    // ---- Status pill ----
    const statusKey = String(data?.status || 'admitted').toLowerCase();
    const statusClassMap = { admitted: 'active', active: 'active', checkout: 'checkout', closed: 'checkout', completed: 'completed', canceled: 'canceled', cancelled: 'canceled' };
    const statusClass = statusClassMap[statusKey] || 'active';
    const statusLabel = tx(`receptionInpatients.tab.status.${statusKey}`, { defaultValue: data?.status || fallback });

    // ---- Diagnosis chips ----
    const diagnosisTags = String(data?.final_diag || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

    // ---- Stay figures ----
    const ratePerDay = parseFloat(data?.rate_per_day) || 0;
    const stayCharge = stayDays !== null && ratePerDay > 0 ? stayDays * ratePerDay : null;
    const cageValue = data?.cage_no || data?.cage?.name || '—';
    const isolatedValue = (data?.is_isolated === null || data?.is_isolated === undefined)
        ? tx('common.tbd', { defaultValue: 'TBD' })
        : (data?.is_isolated ? tx('common.yes', { defaultValue: 'Yes' }) : tx('common.no', { defaultValue: 'No' }));

    const formatAmount = (value) =>
        Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // ---- Guardian one-liner ----
    const guardianName = data?.pet_owner?.full_name ||
        `${data?.pet_owner?.first_name || ''} ${data?.pet_owner?.last_name || ''}`.trim() ||
        fallback;
    const guardianPhone = data?.pet_owner?.phone || fallback;

    return (
        <div className="dashboard-tab-content tab-content-inner dashboard-summary">
            <div className="dashboard-cards-grid">
                {/* ===== Visit: status + serial + admission + expected checkout + doctor ===== */}
                <div className="dashboard-group-header">
                    {tx('receptionInpatients.details.sections.visitInformation', { defaultValue: 'Visit' })}
                </div>
                <div className="info-card-premium info-card-compact summary-full-row">
                    <div className="card-icon"><i className="fas fa-calendar-check"></i></div>
                    <div className="card-details">
                        <div className="summary-visit-line">
                            <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
                            <span className="summary-visit-item">
                                <label>{tx('receptionInpatients.details.labels.serialNumber', { defaultValue: 'Serial Number' })}</label>
                                <p>{data?.in_serial_num || fallback}</p>
                            </span>
                            <span className="summary-visit-item">
                                <label>{tx('receptionInpatients.details.labels.admission', { defaultValue: 'Admission Date' })}</label>
                                <p>{admissionDate}</p>
                            </span>
                            <span className="summary-visit-item">
                                <label>{tx('receptionInpatients.details.labels.expectedCheckout', { defaultValue: 'Expected Checkout' })}</label>
                                <p>{expectedCheckout}</p>
                            </span>
                            <span className="summary-visit-item">
                                <label>{tx('receptionInpatients.details.labels.assignedDoctor', { defaultValue: 'Assigned Doctor' })}</label>
                                <p>{assignedDoctor}</p>
                            </span>
                        </div>
                    </div>
                </div>

                {/* ===== Stay ===== */}
                <div className="dashboard-group-header">
                    {tx('receptionInpatients.details.summarySections.stay', { defaultValue: 'Stay' })}
                </div>
                <div className="info-card-premium info-card-compact summary-full-row">
                    <div className="card-icon"><i className="fas fa-bed"></i></div>
                    <div className="card-details">
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {tx('receptionInpatients.details.labels.cage', { defaultValue: 'Cage' })}
                            </span>
                            <span className="summary-field-value">{cageValue}</span>
                        </div>
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {tx('receptionInpatients.details.summarySections.dayOfStay', { defaultValue: 'Day of stay' })}
                            </span>
                            <span className="summary-field-value">
                                {stayDays !== null
                                    ? tx('receptionInpatients.details.summarySections.dayOfStayValue', { defaultValue: 'Day {{count}}', count: stayDays })
                                    : '—'}
                            </span>
                        </div>
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {tx('receptionInpatients.details.summarySections.ratePerDay', { defaultValue: 'Rate per day' })}
                            </span>
                            <span className="summary-field-value">
                                {ratePerDay > 0 ? formatAmount(ratePerDay) : '—'}
                            </span>
                        </div>
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {tx('receptionInpatients.details.summarySections.stayCharge', { defaultValue: 'Stay charge so far' })}
                            </span>
                            <span className="summary-field-value">
                                {stayCharge !== null ? formatAmount(stayCharge) : '—'}
                            </span>
                        </div>
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {tx('receptionInpatients.details.labels.isolated', { defaultValue: 'Isolated' })}
                            </span>
                            <span className="summary-field-value">{isolatedValue}</span>
                        </div>
                    </div>
                </div>

                {/* ===== Why they're here (same keys as the outpatient summary) ===== */}
                <div className="dashboard-group-header">
                    {tx('receptionOutpatients.details.summarySections.whyHere', { defaultValue: "Why they're here" })}
                </div>
                <div className="info-card-premium info-card-compact summary-full-row">
                    <div className="card-icon"><i className="fas fa-comment-medical"></i></div>
                    <div className="card-details">
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {tx('receptionOutpatients.details.summarySections.mainComplaint', { defaultValue: 'Main complaint' })}
                            </span>
                            <span className="summary-field-value">
                                {data?.main_complaint || data?.reason || (
                                    <span className="summary-muted-text">
                                        {tx('receptionOutpatients.details.summarySections.noComplaint', { defaultValue: 'No complaint recorded' })}
                                    </span>
                                )}
                            </span>
                        </div>
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {tx('receptionOutpatients.details.summarySections.initialImpression', { defaultValue: 'Initial impression' })}
                            </span>
                            <span className="summary-field-value">
                                {data?.initial_diag || (
                                    <span className="summary-muted-text">—</span>
                                )}
                            </span>
                        </div>
                        <div className="summary-field">
                            <span className="summary-field-label">
                                {tx('receptionOutpatients.details.summarySections.finalDiagnosis', { defaultValue: 'Final diagnosis' })}
                            </span>
                            <span className="summary-field-value">
                                {diagnosisTags.length > 0 ? (
                                    <span className="diag-tags">
                                        {diagnosisTags.map((tag, idx) => (
                                            <span key={idx} className="diag-tag">{tag}</span>
                                        ))}
                                    </span>
                                ) : (
                                    <span className="summary-muted-text">
                                        {tx('receptionOutpatients.details.summarySections.noDiagnosis', { defaultValue: 'No diagnosis recorded yet' })}
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ===== Guardian: one card ===== */}
                <div className="dashboard-group-header">
                    {tx('receptionInpatients.details.sections.guardianInformation', { defaultValue: 'Guardian' })}
                </div>
                <div className="info-card-premium info-card-compact summary-full-row">
                    <div className="card-icon"><i className="fas fa-user-tie"></i></div>
                    <div className="card-details">
                        <p className="summary-pet-line">
                            {guardianName} · <i className="fas fa-phone-alt" style={{ fontSize: '0.8rem', color: '#00D4B3' }}></i> {guardianPhone}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardTab;
